// The asset's product and description are withheld with the client's name when the viewer can't
// open the client.
export type ProviderReferenceHolder = {
  asset_id: string;
  asset_product: string | null;
  asset_description: string | null;
  client_id: string;
  client_full_name: string | null;
  adviser_full_name: string | null;
  is_joint: boolean;
};

type HolderDescription = {
  label: string;
  path: string | null;
};

const describeHolderByName = ({ client_full_name, adviser_full_name }: ProviderReferenceHolder) => {
  if (client_full_name) {
    return adviser_full_name
      ? `${client_full_name} (adviser ${adviser_full_name})`
      : client_full_name;
  }
  return adviser_full_name ? `a client of ${adviser_full_name}` : 'another client';
};

export const describeProviderReferenceHolders = (holders: ProviderReferenceHolder[]) =>
  new Intl.ListFormat('en-GB', { style: 'long', type: 'conjunction' }).format(
    holders.map(describeHolderByName),
  );

// Descriptions are shown as entered: feed valuations set some, and they carry provider and
// product names whose casing matters.
export const describeHolder = (holder: ProviderReferenceHolder): HolderDescription => {
  if (holder.client_full_name) {
    return {
      label: `${holder.client_full_name}'s ${holder.asset_description ?? 'asset'}`,
      path: `/client/${holder.client_id}/financial-items/${holder.asset_product}/${holder.asset_id}`,
    };
  }
  return {
    label: holder.adviser_full_name
      ? `an asset for a client of ${holder.adviser_full_name}`
      : 'an asset for another client',
    path: null,
  };
};

export const describeSharedReferenceResolution = ({
  cannotBeJoint,
  ownersEnabled,
  feedMatchingRules,
}: {
  cannotBeJoint: boolean;
  ownersEnabled: boolean;
  feedMatchingRules: boolean;
}) => {
  // Without the feed matching rules, a shared reference stops automatic valuations.
  if (cannotBeJoint) {
    return feedMatchingRules
      ? "A pension or ISA can't be held jointly, so one of the references is probably wrong."
      : "A pension can't be held jointly, so one of the references is probably wrong. Automatic valuations are paused until it's corrected.";
  }
  const jointControl = ownersEnabled ? 'turn on "Joint asset"' : 'answer yes to "Is this joint?"';
  const paused = feedMatchingRules ? '' : ' Automatic valuations are paused until then.';
  return `If this is a joint holding, ${jointControl} on each client's copy; otherwise one of the references is probably wrong.${paused}`;
};
